import { ArrowRight, ChevronRight } from "lucide-react";
import { HighlightBanner, Section } from "@/app/components/brand-scaffold";
import {
  operatorEvolutionSteps,
  operatorPersonas,
} from "@/design-system/registry/homepage";

/**
 * S4 · Operator Personas — 四个超级个体在赚钱（人格化）
 *
 * Visual hierarchy (top → bottom):
 *   1. Evolution timeline at the top  — THE focal anchor (一个人 → 一家公司)
 *   2. 4 persona cards below          — single-line "before → after" reversal
 *      (no labels, no dividers; rely on strikethrough + arrow + bold weight)
 *   3. Closing one-liner banner
 *
 * Goal: less reading, more visual punch. The eye should land on the timeline
 * first (it carries the section's whole thesis), then sweep across 4 cards
 * scanning only the "after" lines (which are the only bold rows).
 */
export function HomepageOperatorPersonas() {
  return (
    <Section
      id="operators"
      eyebrow="WHO IS RUNNING A HOTLINE"
      title="把「只有你会的那部分」做成数字分身"
      description="Hotline 不是面向程序员的远程函数，而是面向「任何只有你会的事」的产品形态。"
    >
      {/* 1) Evolution timeline — section-level focal anchor */}
      <div className="border-[6px] border-black bg-white/90 backdrop-blur-sm shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]">
        <div className="border-b-4 border-black bg-black px-5 py-3 sm:px-6 sm:py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="bg-[#A3E635] text-black border-2 border-[#A3E635] px-2 py-0.5 text-[10px] font-black tracking-widest uppercase">
              Evolution
            </span>
            <span className="text-sm sm:text-base font-black uppercase text-white tracking-tight">
              一个人 → 一家公司
            </span>
          </div>
          <span className="text-[10px] font-black uppercase tracking-widest text-white/60">
            5 stages · single track
          </span>
        </div>
        <div className="p-4 sm:p-5 md:p-6 lg:p-7">
          {/* <md → horizontal snap scroll (one card at a time, time-axis preserved);
              md+ → flat row, all 5 stages visible side-by-side, connected by chevrons. */}
          <div className="flex md:flex-nowrap items-stretch gap-3 md:gap-1.5 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none -mx-2 px-2 md:mx-0 md:px-0 scrollbar-thin">
            {operatorEvolutionSteps.map((step, idx) => {
              const isLast = idx === operatorEvolutionSteps.length - 1;
              return (
                <div
                  key={step.stage}
                  className="flex items-stretch flex-shrink-0 md:flex-1 md:min-w-0 gap-2 md:gap-1.5 snap-start w-[80%] sm:w-[46%] md:w-auto"
                >
                  <div className="flex-1 min-w-0 border-4 border-black bg-white p-3.5 sm:p-4 md:p-2.5 lg:p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] flex flex-col items-start justify-center gap-2 md:gap-1.5 lg:gap-2 min-h-[88px] md:min-h-[96px]">
                    <p className="text-sm md:text-xs lg:text-[15px] xl:text-base font-black text-black leading-tight">
                      {step.stage}
                    </p>
                    <p className="text-[10px] md:text-[9px] lg:text-[10px] font-black uppercase tracking-widest text-zinc-500 leading-none">
                      {step.meta}
                    </p>
                  </div>
                  {!isLast && (
                    <div className="hidden md:flex items-center justify-center w-3 shrink-0">
                      <ChevronRight className="h-5 w-5 text-black" strokeWidth={3} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {/* swipe hint only when scrolling is active (i.e. <md) */}
          <p className="md:hidden mt-3 text-[10px] font-black uppercase tracking-widest text-zinc-500">
            ← swipe →
          </p>
        </div>
      </div>

      {/* Connective tissue — sets up the 4 examples below */}
      <div className="mt-12 mb-6 border-l-8 border-black pl-5 max-w-3xl">
        <p className="text-base sm:text-lg font-black text-black leading-relaxed">
          那么，「一个人」长什么样？下面是 4 种已经在赚钱的超级个体。
        </p>
      </div>

      {/* 2) Persona cards — minimum-information version */}
      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
        {operatorPersonas.map((p) => (
          <article
            key={p.num}
            className={`flex flex-col border-4 border-black ${p.bg} ${p.hoverBg} ${p.text} p-5 sm:p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transition-all duration-200 hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]`}
          >
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-black leading-none opacity-70">
                {p.num}
              </span>
              <h3 className="text-sm font-black uppercase tracking-tight leading-tight opacity-95">
                {p.role}
              </h3>
            </div>

            {/* Single-line reversal: strikethrough before → bold after */}
            <div className="mt-5 space-y-3 text-sm font-bold leading-snug">
              <p className="line-through opacity-60">{p.before}</p>
              <div className="flex items-start gap-2">
                <ArrowRight className="h-4 w-4 mt-1 shrink-0 opacity-80" strokeWidth={3} />
                <p className="font-black">{p.after}</p>
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* 3) Single-line closing banner */}
      <div className="mt-10">
        <HighlightBanner
          badgeText="UPSIDE"
          badgeColor="bg-[#8B5CF6]"
          shadowClass="shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"
        >
          <p className="text-base sm:text-lg font-black text-zinc-900 leading-relaxed">
            Hotline 不是小公司的廉价替代品，
            <span className="bg-[#A3E635] px-1.5 py-0.5">
              而是一个人通向一家公司的过渡路径
            </span>
            。
          </p>
        </HighlightBanner>
      </div>
    </Section>
  );
}
